import {
  ArrowLeft,
  CaretRight,
  Clock,
  Lightning,
  PlusCircle,
  Stack,
  Tag,
  Trash,
  TreeStructure,
} from "@phosphor-icons/react"
import { startCase } from "lodash"
import React from "react"
import SearchField from "~/components/planning/SearchField"
import { Button, IconButton } from "~/components/reusable/Button"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import Select from "~/components/reusable/Select"
import {
  type Filter,
  type FilterFieldType,
  filterByLabels,
  getFilterIcon,
  getFilterOperators,
} from "~/utils/filter"
import { trackEvent } from "~/hooks/useTrackingService"
import { useFilterFieldsOverride } from "~/hooks/useFilterFieldsOverride"
import { cn } from "~/utils/helpers"

import { FilterGroupEditor } from "./FilterGroupEditor"
import { FilterPill } from "./FilterPill"
import { FilterValueInput } from "./FilterValueInput"
import { type FilterMainItem, useFilterPopover } from "~/hooks/useFilterPopover"
import { QuickFiltersList } from "./QuickFiltersList"
import { KeyboardShortcut } from "~/components/sidebar/search/KeyboardShortcut"
import { Separator } from "~/components/reusable/Separator"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { HOTKEYS } from "~/constants/hotkeys"

interface FilterPopoverBaseProps {
  editingFilter?: Filter
  editingFilterIndex?: number
  customTrigger?: React.ReactNode
  align?: "start" | "center" | "end"
  saveInDB?: boolean
  shortcutEnabled?: boolean
  registerSearchCommands?: boolean
  editGroup?: boolean
  extraFilterFields?: FilterFieldType[]
  extraFilterOptions?: React.ReactNode
}

function FilterPopoverBase({
  editingFilter,
  editingFilterIndex,
  customTrigger,
  align = "start",
  saveInDB = true,
  shortcutEnabled = false,
  registerSearchCommands = false,
  editGroup = false,
  extraFilterFields,
  extraFilterOptions,
}: FilterPopoverBaseProps) {
  const {
    filterSearch,
    setFilterSearch,
    showQuickFilters,
    setShowQuickFilters,
    focusedIndex,
    onOpenChange,
    open,
    focusedQuickFilterIndex,
    isFilterValid,
    getTriggerText,
    applyFilter,
    removeFilter,
    handleQuickFilterClick,
    handleQuickFiltersRowClick,
    selectedFilterField,
    selectedFilterOperator,
    selectedFilterLevel,
    selectedFilterValue,
    selectedFilterValues,
    resetFilterSelection,
    setSelectedFilterOperator,
    setSelectedFilterLevel,
    setSelectedFilterValue,
    setSelectedFilterValues,
    mainItems,
    handleMainItemActivate,
    handleEscapeKeyDown,
    hasRecents,
    filters,
    setFilters,
    allFilterFields,
    quickFilterFields,
    nonBooleanQuickFilters,
  } = useFilterPopover({
    editingFilter,
    editingFilterIndex,
    shortcutEnabled,
    registerSearchCommands,
    saveInDB,
    extraFilterFields,
  })

  const fieldsOverride = useFilterFieldsOverride()

  const [advancedMode, setAdvancedMode] = React.useState(editGroup)
  const [draftFilters, setDraftFilters] = React.useState<Filter[]>([])
  const creatingGroup = advancedMode && !editGroup

  const commitDraftGroup = () => {
    if (draftFilters.length === 0) return
    const usedGroupIds = filters.map((f) => f.groupId).filter((g): g is number => g != null)
    const newGroupId = (usedGroupIds.length ? Math.max(...usedGroupIds) : 0) + 1
    const grouped = draftFilters.map((f) => ({
      ...f,
      groupId: draftFilters.length > 1 ? newGroupId : undefined,
    }))
    setFilters([...filters, ...grouped])
    setDraftFilters([])
  }

  const editGroupId = editingFilter?.groupId
  const isScopedGroup = editGroup && editGroupId != null
  const scopedFilters = filters.filter((f) => f.groupId === editGroupId)
  const handleScopedChange = (next: Filter[]) => {
    const start = filters.findIndex((f) => f.groupId === editGroupId)
    if (start === -1) {
      setFilters([...filters, ...next])
      return
    }
    let end = start
    while (end < filters.length && filters[end]?.groupId === editGroupId) end++
    const tagged = next.map((f) => ({ ...f, groupId: next.length > 1 ? editGroupId : undefined }))
    setFilters([...filters.slice(0, start), ...tagged, ...filters.slice(end)])
  }

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      commitDraftGroup()
      setAdvancedMode(editGroup)
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <ShortcutTooltip
        label="Filter"
        shortcut={HOTKEYS.openFilterPopover.hotkey}
        side="bottom"
        disabled={!shortcutEnabled}
      >
        <PopoverTrigger asChild>
          {customTrigger ? (
            customTrigger
          ) : (
            <FilterPill
              size="xs"
              variant="primarySoft"
              icon={
                editingFilter ? getFilterIcon(editingFilter.field, allFilterFields) : <PlusCircle />
              }
              showRemove={!!editingFilter}
              onRemove={() => {
                if (editingFilter) {
                  removeFilter()
                  onOpenChange(false)
                  trackEvent("Filter Removed", {
                    Filter: selectedFilterField?.name ?? "",
                  })
                }
              }}
              onClick={() => {
                trackEvent("Filter Popover Opened", {
                  Source: "Click",
                  Shortcut: "",
                })
              }}
            >
              {getTriggerText()}
            </FilterPill>
          )}
        </PopoverTrigger>
      </ShortcutTooltip>
      <PopoverContent
        align={align}
        className={cn(
          "p-0 relative max-h-[min(calc(var(--radix-popover-content-available-height)-160px),800px))]",
          advancedMode
            ? "w-fit min-w-[420px] max-w-[700px] overflow-y-auto"
            : "flex flex-col divide-y divide-border-base w-[360px] max-w-[420px] min-w-[280px] overflow-hidden",
        )}
        sideOffset={8}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {advancedMode && (
          <FilterGroupEditor
            filters={creatingGroup ? draftFilters : isScopedGroup ? scopedFilters : filters}
            onFiltersChange={
              creatingGroup ? setDraftFilters : isScopedGroup ? handleScopedChange : setFilters
            }
            isLocal={creatingGroup || isScopedGroup ? true : !saveInDB}
            onBack={editGroup ? () => onOpenChange(false) : () => setAdvancedMode(false)}
            onApply={
              creatingGroup
                ? () => {
                    commitDraftGroup()
                    onOpenChange(false)
                  }
                : () => onOpenChange(false)
            }
            enableQuickFilters={false}
            enableGroups={false}
            extraFilterFields={extraFilterFields}
          />
        )}
        {!advancedMode && selectedFilterField && (
          <>
            <div className="flex flex-row items-center justify-between gap-2 label-base text-fg-subtle px-3 py-2 relative">
              <div className="flex flex-row items-center gap-2">
                <IconButton
                  size="sm"
                  onClick={() => {
                    resetFilterSelection()
                  }}
                  variant="transparent"
                >
                  <ArrowLeft size={16} />
                </IconButton>
                {selectedFilterField.icon ?? <Tag />}
                {startCase(selectedFilterField.name)}
                <Select
                  options={getFilterOperators(
                    selectedFilterField.value === "DynamicFilterField"
                      ? (selectedFilterField.dynamicValue ?? "")
                      : selectedFilterField.value,
                    allFilterFields,
                  )}
                  value={selectedFilterOperator}
                  getOptionValue={(operator) => operator.value}
                  getOptionLabel={(operator) => operator.name}
                  onChange={(operator) => setSelectedFilterOperator(operator)}
                  size="sm"
                  transparent
                />
              </div>
              {editingFilter && (
                <IconButton
                  size="sm"
                  variant="transparent"
                  onClick={() => {
                    removeFilter()
                    onOpenChange(false)
                    trackEvent("Filter Removed", {
                      Filter: selectedFilterField?.name ?? "",
                    })
                  }}
                >
                  <Trash />
                </IconButton>
              )}
            </div>

            {/* Value input section */}
            {selectedFilterOperator && (
              <div className="flex flex-row items-center gap-2 justify-between px-3 py-2">
                <div className="flex-1">
                  <FilterValueInput
                    filter={{
                      field:
                        selectedFilterField.value === "DynamicFilterField"
                          ? (selectedFilterField.dynamicValue ?? "")
                          : selectedFilterField.value,
                      operator: selectedFilterOperator.value,
                      value: selectedFilterValue,
                      values: selectedFilterValues,
                      by: selectedFilterLevel ?? "variantId",
                    }}
                    onValueChange={setSelectedFilterValue}
                    onValuesChange={setSelectedFilterValues}
                    extraFilterFields={extraFilterFields}
                  />
                </div>
              </div>
            )}

            {!fieldsOverride && (
              <div className="flex flex-row items-center gap-2 justify-between px-3 py-2">
                <div className="label-base text-fg-subtle">Filter level</div>
                <Select
                  onChange={(f) => f && setSelectedFilterLevel(f.value)}
                  value={filterByLabels.find(({ value: val }) => val === selectedFilterLevel)}
                  isDisabled={!selectedFilterField}
                  options={filterByLabels}
                  size="sm"
                  transparent
                  leftIcon={<Stack />}
                />
              </div>
            )}

            {/* Apply button */}
            {isFilterValid() && (
              <div className="px-3 py-2">
                <Button size="sm" onClick={applyFilter} className="w-full">
                  {editingFilter ? "Update filter" : "Apply filter"}
                </Button>
              </div>
            )}
          </>
        )}
        {!advancedMode && !selectedFilterField && (
          <>
            <SearchField
              placeholder={showQuickFilters ? "Search quick filters" : "Search filters"}
              initialSearchText={filterSearch}
              onSearchTextChange={setFilterSearch}
              className="shadow-none border-none my-1 focus:shadow-none"
              containerClassName="w-full"
              showInfoElement={false}
              debounceTime={100}
              variant="invisible"
            />
            {!showQuickFilters && (
              <>
                <button
                  type="button"
                  onClick={() => setAdvancedMode(true)}
                  className="flex w-full flex-row items-center gap-2 px-3 py-2 label-base text-left hover:bg-base-hover cursor-pointer"
                >
                  <TreeStructure className="text-fg-subtle" />
                  Advanced filters
                </button>
                <Separator orientation="horizontal" />
                {extraFilterOptions && (
                  <>
                    <div className="px-3 py-2">{extraFilterOptions}</div>
                    <Separator orientation="horizontal" />
                  </>
                )}
              </>
            )}
            <div className="flex flex-col p-0 flex-1 min-h-0 overflow-y-auto">
              {showQuickFilters ? (
                <QuickFiltersList
                  showQuickFilters={showQuickFilters}
                  setShowQuickFilters={setShowQuickFilters}
                  quickFilterFields={quickFilterFields}
                  nonBooleanQuickFilters={nonBooleanQuickFilters}
                  focusedQuickFilterIndex={focusedQuickFilterIndex}
                  onQuickFilterClick={handleQuickFilterClick}
                  onQuickFiltersRowClick={handleQuickFiltersRowClick}
                />
              ) : mainItems.length === 0 ? (
                <div className="px-3 py-6 text-center label-base text-fg-subtle">
                  {filterSearch ? `No filters match "${filterSearch}"` : "No filters available"}
                </div>
              ) : (
                <FilterMainList
                  items={mainItems}
                  focusedIndex={focusedIndex}
                  onActivate={handleMainItemActivate}
                  showRecentsHeader={hasRecents && !filterSearch}
                />
              )}
            </div>
            <FilterKeyboardHints showQuickFilters={showQuickFilters} />
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

interface FilterMainListProps {
  items: FilterMainItem[]
  focusedIndex: number
  onActivate: (item: FilterMainItem) => void
  showRecentsHeader: boolean
}

function FilterMainList({
  items,
  focusedIndex,
  onActivate,
  showRecentsHeader,
}: FilterMainListProps) {
  const lastRecentIdx = items.findLastIndex(
    (i) => i.kind === "recent-field" || i.kind === "recent-quick",
  )
  return (
    <div className="p-1">
      {showRecentsHeader && (
        <div className="flex flex-row items-center gap-2 px-2 pt-1 pb-0.5 label-base text-fg-subtle">
          <Clock />
          Recent
        </div>
      )}
      {items.map((item, index) => {
        const isFocused = focusedIndex === index
        const showSeparator = showRecentsHeader && index === lastRecentIdx + 1 && lastRecentIdx >= 0
        return (
          <React.Fragment key={mainItemKey(item, index)}>
            {showSeparator && <div className="my-1 border-t border-border-base" />}
            <div
              ref={(el) => {
                if (isFocused && el) {
                  el.scrollIntoView({ block: "nearest" })
                }
              }}
              onClick={() => onActivate(item)}
              className={`px-2 py-1.5 hover:bg-base-hover cursor-pointer label-base rounded-[6px] transition-colors flex flex-row items-center justify-between gap-2 ${
                isFocused ? "bg-base-hover ring-1 ring-border-base" : ""
              }`}
            >
              <FilterMainRowContent item={item} />
              {item.kind === "field" ||
              item.kind === "recent-field" ||
              item.kind === "quick-row" ? (
                <CaretRight />
              ) : null}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

function FilterMainRowContent({ item }: { item: FilterMainItem }) {
  switch (item.kind) {
    case "quick-row":
      return (
        <div className="flex flex-row items-center gap-2">
          <Lightning />
          Quick filters
        </div>
      )
    case "field":
    case "recent-field":
      return (
        <div className="flex flex-row items-center gap-2">
          {item.field.icon ?? <Tag />}
          {startCase(item.field.name)}
        </div>
      )
    case "recent-quick":
      return (
        <div className="flex flex-row items-center gap-2 min-w-0">
          {item.quick.icon ?? <Tag />}
          <span className="truncate">{item.quick.name}</span>
        </div>
      )
    case "quick-match":
      return (
        <div className="flex flex-row items-center gap-2 min-w-0">
          <Lightning className="text-fg-subtle shrink-0" />
          <span className="text-fg-subtle shrink-0">Quick filters</span>
          <CaretRight size={10} className="text-fg-subtle shrink-0" />
          <span className="truncate">{item.quick.name}</span>
        </div>
      )
    case "enum-match":
      return (
        <div className="flex flex-row items-center gap-2 min-w-0">
          {item.field.icon ?? <Tag className="text-fg-subtle shrink-0" />}
          <span className="text-fg-subtle shrink-0">{startCase(item.field.name)}</span>
          <CaretRight size={10} className="text-fg-subtle shrink-0" />
          <span className="truncate">{item.value}</span>
        </div>
      )
  }
}

function mainItemKey(item: FilterMainItem, index: number) {
  switch (item.kind) {
    case "quick-row":
      return "quick-row"
    case "field":
    case "recent-field":
      return `${item.kind}::${item.field.value}::${item.field.dynamicValue ?? ""}`
    case "recent-quick":
    case "quick-match":
      return `${item.kind}::${item.isNonBoolean ? "nb" : "b"}::${item.quick.value}::${index}`
    case "enum-match":
      return `enum-match::${item.field.value}::${item.field.dynamicValue ?? ""}::${item.value}`
  }
}

function FilterKeyboardHints({ showQuickFilters }: { showQuickFilters: boolean }) {
  return (
    <div className="bg-subtle px-3 py-2 flex flex-row justify-end items-center text-sm gap-3">
      <KeyboardShortcut label="Navigate" keys={["↓", "↑"]} />
      <Separator orientation="vertical" className="h-4 bg-border-base" />
      <KeyboardShortcut label="Select" keys="↵" />
      <Separator orientation="vertical" className="h-4 bg-border-base" />
      <KeyboardShortcut label={showQuickFilters ? "Back" : "Close"} keys="esc" />
    </div>
  )
}

export default FilterPopoverBase
